/**
 * Service-worker registration — the web half of "works offline".
 *
 * Only the browser needs this. Inside the Capacitor shell the whole bundle is
 * already on the filesystem, and on iOS a service worker cannot be registered
 * against the `capacitor://` scheme at all, so we skip it entirely there.
 *
 * `registerType: 'autoUpdate'` (vite.config.ts) means a new deploy is picked up
 * on the next launch without asking: the worker installs in the background, and
 * `onNeedRefresh` is never fired. That is the behaviour we want — the app should
 * quietly become current, not interrogate the user about it.
 */
import { isNativeApp } from './platform';

export async function registerServiceWorker(): Promise<void> {
  if (isNativeApp) return;
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
  try {
    const { registerSW } = await import('virtual:pwa-register');
    registerSW({
      immediate: true,
      /**
       * Ask for a new worker WHEN THE USER COMES BACK, not on a timer.
       *
       * A browser only checks for a new service worker on navigation or roughly
       * once a day, and this is a single-page app: someone who leaves a tab open
       * can miss deploys for hours and keep being served the precached bundle.
       * That is the "I keep seeing the old version" problem, and `?v=` cannot
       * touch it — `ignoreURLParametersMatching` in vite.config.ts deliberately
       * makes the worker ignore that parameter so offline chants resolve at all.
       *
       * `autoUpdate` reloads the page as soon as a new worker takes over, so
       * WHEN we check matters: checking on a timer would yank the page out from
       * under someone mid-recitation. Regaining visibility (app foregrounded,
       * tab refocused) and regaining the network are the two moments a reload
       * costs nothing, so we check there and nowhere else.
       */
      onRegisteredSW(_url, r) {
        if (!r) return;
        const check = () => { void r.update().catch(() => {}); };
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') check();
        });
        window.addEventListener('online', check);
      },
    });
  } catch {
    // A failed registration costs offline support, nothing else. Never let it
    // take down the app.
  }
}
